export interface Probe {
  durationSec: number;
  /** JPEG poster grabbed from the file, or null if the browser wouldn't decode it. */
  thumb: Blob | null;
}

const THUMB_MAX_WIDTH = 640;
const THUMB_AT_SECONDS = 1;

/**
 * Reads duration and grabs a poster frame in the browser, so the server needs no
 * ffmpeg — which keeps the container small and the VM free of a media toolchain.
 * Failures are non-fatal: a missing thumb just falls back to the gradient.
 */
export function probeVideoFile(file: File): Promise<Probe> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    let settled = false;
    const finish = (probe: Probe) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      resolve(probe);
    };

    // Some containers (large .mov especially) never fire seeked in some browsers.
    const timeout = window.setTimeout(
      () => finish({ durationSec: durationOf(video), thumb: null }),
      15_000,
    );

    video.addEventListener('error', () => {
      window.clearTimeout(timeout);
      finish({ durationSec: 0, thumb: null });
    });

    video.addEventListener('loadedmetadata', () => {
      // Seek slightly in: frame 0 of an action cam is often a black lens-cap frame.
      video.currentTime = Math.min(THUMB_AT_SECONDS, Math.max(durationOf(video) - 0.1, 0));
    });

    video.addEventListener('seeked', () => {
      window.clearTimeout(timeout);
      const durationSec = durationOf(video);
      try {
        const scale = Math.min(1, THUMB_MAX_WIDTH / (video.videoWidth || THUMB_MAX_WIDTH));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx || canvas.width === 0) {
          finish({ durationSec, thumb: null });
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => finish({ durationSec, thumb: blob }), 'image/jpeg', 0.8);
      } catch {
        finish({ durationSec, thumb: null });
      }
    });
  });
}

function durationOf(video: HTMLVideoElement): number {
  return Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
}
